import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import {
    MdCheckCircle, MdError, MdUpload, MdSave, MdRefresh, MdLock,
    MdComputer, MdSmartphone, MdHeadset, MdPower, MdTv, MdPrint, MdWifi,
    MdChair, MdTableRestaurant, MdToys, MdLightbulb, MdDelete, MdVideocam, MdDirectionsCar, MdClass
} from 'react-icons/md';

const CenterFacilities = () => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('computer');
    const [uploadingId, setUploadingId] = useState(null);
    const [message, setMessage] = useState('');
    const [isLocked, setIsLocked] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await api.get('/center-asset-items');
            setData(response.data.items || {});
            setIsLocked(response.data.locked || false);
            setSubmittedAt(response.data.submitted_at || null);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching assets:', error);
            setLoading(false);
        }
    };

    const handleFinalSubmit = async () => {
        setSubmitting(true);
        try {
            const response = await api.post('/center-asset-items/final-submit');
            setIsLocked(true);
            setSubmittedAt(response.data.submitted_at);
            setShowConfirmModal(false);
            setMessage(response.data.message);
            setTimeout(() => setMessage(''), 5000);
        } catch (error) {
            console.error('Final submit failed:', error);
            alert(error.response?.data?.error || 'Failed to submit');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (category, index, field, value) => {
        // Optimistic update
        setData(prev => {
            const catData = { ...prev[category] };
            const items = [...catData.items];
            const itemIndex = items.findIndex(i => i.item_index === index);

            if (itemIndex > -1) {
                items[itemIndex] = { ...items[itemIndex], [field]: value };
            }

            return { ...prev, [category]: { ...catData, items } };
        });
    };

    const handleSpecUpdate = (category, index, key, value) => {
        setData(prev => {
            const catData = { ...prev[category] };
            const items = [...catData.items];
            const itemIndex = items.findIndex(i => i.item_index === index);

            if (itemIndex > -1) {
                const currentSpecs = items[itemIndex].specifications || {};
                items[itemIndex] = {
                    ...items[itemIndex],
                    specifications: { ...currentSpecs, [key]: value }
                };
            }

            return { ...prev, [category]: { ...catData, items } };
        });
    };

    const saveItem = async (category, item) => {
        setUploadingId(`${category}-${item.item_index}`);
        const formData = new FormData();
        formData.append('category', category);
        formData.append('item_index', item.item_index);
        formData.append('status', item.status || 'working');

        if (item.model_no) formData.append('model_no', item.model_no);
        if (item.serial_no) formData.append('serial_no', item.serial_no);
        if (item.specifications) formData.append('specifications', JSON.stringify(item.specifications));

        if (item.newPhoto) {
            formData.append('photo', item.newPhoto);
        }

        try {
            const res = await api.post('/center-asset-items', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Update with server response (e.g. image URL)
            setData(prev => {
                const catData = { ...prev[category] };
                const items = [...catData.items];
                const itemIndex = items.findIndex(i => i.item_index === item.item_index);
                if (itemIndex > -1) {
                    items[itemIndex] = { ...res.data.item, newPhoto: null }; // Clear pending photo
                }
                return { ...prev, [category]: { ...catData, items } };
            });

            setMessage('Saved successfully!');
            setTimeout(() => setMessage(''), 2000);
        } catch (error) {
            console.error('Save failed', error);
            alert('Failed to save item.');
        } finally {
            setUploadingId(null);
        }
    };

    const getIcon = (key) => {
        switch (key) {
            case 'computer': return <MdComputer />;
            case 'mobile': return <MdSmartphone />;
            case 'headphone': return <MdHeadset />;
            case 'power_backup': return <MdPower />;
            case 'tv': return <MdTv />;
            case 'printer': return <MdPrint />;
            case 'internet': return <MdWifi />;
            case 'chair': return <MdChair />;
            case 'table': return <MdTableRestaurant />;
            case 'fan': return <MdToys />;
            case 'led_bulb': return <MdLightbulb />;
            case 'batch_desk': return <MdClass />;
            case 'dustbin': return <MdDelete />;
            case 'cctv': return <MdVideocam />;
            case 'auto_rickshaw': return <MdDirectionsCar />;
            default: return <MdCheckCircle />;
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading assets...</div>;

    const getCategoryConfig = (category) => {
        const configs = {
            computer: { model: 'Model No', serial: 'Serial No', extra: { ram: 'RAM', hdd: 'HDD/SSD' } },
            mobile: { model: 'Model Name', serial: 'IMEI / Serial', extra: { ram: 'RAM', storage: 'Storage' } },
            headphone: { model: 'Brand / Model', serial: 'Serial / ID (Optional)', extra: { type: 'Type (Wired/Wireless)' } },
            power_backup: { model: 'Brand / Make', serial: 'Battery Serial', extra: { capacity: 'Capacity (VA)' } },
            tv: { model: 'Brand / Model', serial: 'Serial No', extra: { size: 'Size (Inches)' } },
            printer: { model: 'Model Name', serial: 'Serial No', extra: { type: 'Type (Laser/Ink)' } },
            internet: { model: 'ISP Name', serial: 'Plan / User ID', extra: { speed: 'Speed (Mbps)' } },
            chair: { model: 'Total Quantity', serial: null, extra: {} },
            table: { model: 'Total Quantity', serial: null, extra: {} },
            fan: { model: 'Brand', serial: 'Type (Ceiling/Table)', extra: { speed: 'Speed Settings' } },
            led_bulb: { model: 'Brand', serial: 'Wattage', extra: {} },
            batch_desk: { model: 'Total Quantity', serial: null, extra: {} },
            dustbin: { model: 'Total Quantity', serial: null, extra: {} },
            cctv: { model: 'Model No', serial: 'Serial No', extra: { type: 'Type (Dome/Bullet)' } },
            auto_rickshaw: { model: 'Registration No', serial: 'Driver Name', extra: {} }
        };
        return configs[category] || { model: 'Model No', serial: 'Serial No', extra: {} };
    };

    const config = getCategoryConfig(activeCategory);
    const currentCategoryData = data[activeCategory];
    const isQuantityOnly = ['chair', 'table', 'batch_desk', 'dustbin'].includes(activeCategory);

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen text-gray-800">
            <div className="flex flex-row justify-between items-center mb-4 md:mb-6 gap-2">
                <div className="flex-1">
                    <h1 className="text-sm md:text-2xl font-bold flex items-center gap-1 md:gap-2 text-gray-800 flex-wrap">
                        <MdCheckCircle className="text-blue-600 text-base md:text-2xl" />
                        <span>Center Available Assets</span>
                        {isLocked && (
                            <span className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                <MdLock size={12} className="md:hidden" />
                                <MdLock size={16} className="hidden md:block" />
                                Locked
                            </span>
                        )}
                    </h1>
                    <p className="hidden md:block text-gray-500 text-sm mt-1">Track individual devices, specific properties, and photos for every single item.</p>
                    {isLocked && submittedAt && (
                        <p className="text-[10px] md:text-xs text-gray-400 mt-0.5 md:mt-1">Submitted: {new Date(submittedAt).toLocaleString()}</p>
                    )}
                </div>
                <div className="flex flex-col items-end gap-2">
                    {message && <div className="bg-green-100 text-green-700 px-2.5 py-1 md:px-4 md:py-2 rounded border border-green-200 text-[10px] md:text-sm whitespace-nowrap">{message}</div>}
                    {!isLocked && (
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            className="px-2.5 py-1 md:px-6 md:py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-1 md:gap-2 text-[10px] md:text-base whitespace-nowrap"
                        >
                            <MdLock size={14} className="md:hidden" />
                            <MdLock size={20} className="hidden md:block" />
                            Final Submit
                        </button>
                    )}
                </div>
            </div>

            {/* Category Tabs */}
            <div className="flex overflow-x-auto gap-2 mb-6 pb-2 no-scrollbar">
                {Object.keys(data).map(key => (
                    <button
                        key={key}
                        onClick={() => setActiveCategory(key)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all shadow-sm ${activeCategory === key
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                            : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                            }`}
                    >
                        {getIcon(key)}
                        {data[key].config.label}
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full ml-1 text-gray-600">
                            {data[key].config.count}
                        </span>
                    </button>
                ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
                {currentCategoryData?.items.map((item) => (
                    <div key={item.item_index} className={`bg-white rounded-xl overflow-hidden border shadow-sm transition-all hover:shadow-md ${item.exists ? 'border-blue-200' : 'border-gray-200'}`}>
                        {/* Header */}
                        <div className="p-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                            <span className="font-bold text-gray-700">
                                {currentCategoryData.config.label} {item.item_index}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded uppercase font-bold ${item.status === 'working' ? 'bg-green-100 text-green-700' :
                                item.status === 'faulty' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                                }`}>
                                {item.status || 'Pending'}
                            </span>
                        </div>

                        {/* Image Upload */}
                        <div className="relative h-48 bg-gray-100 group border-b border-gray-100">
                            {item.newPhoto ? (
                                // Preview new photo
                                <img src={URL.createObjectURL(item.newPhoto)} className="w-full h-full object-cover opacity-50" />
                            ) : item.image_url ? (
                                <img src={item.image_url} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 flex-col gap-2">
                                    <MdUpload size={32} />
                                    <span className="text-xs">No Photo</span>
                                </div>
                            )}

                            <label className={`absolute inset-0 flex flex-col items-center justify-center bg-black/50 ${isLocked ? 'opacity-40 cursor-not-allowed' : 'opacity-0 group-hover:opacity-100 cursor-pointer'} transition text-white`}>
                                {!isLocked && (
                                    <>
                                        <MdUpload size={40} className="mb-2" />
                                        <span className="text-sm font-medium">Take Photo / Upload</span>
                                    </>
                                )}
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    capture="environment"
                                    disabled={isLocked}
                                    onChange={(e) => {
                                        if (e.target.files[0]) {
                                            handleUpdate(activeCategory, item.item_index, 'newPhoto', e.target.files[0]);
                                        }
                                    }}
                                />
                            </label>
                        </div>

                        {/* Form */}
                        <div className="p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                                {config.model && (
                                    <div className={!config.serial ? "col-span-2" : ""}>
                                        <label className="text-[10px] uppercase text-gray-500 font-bold">{config.model}</label>
                                        <input
                                            type="text"
                                            value={item.model_no || ''}
                                            onChange={(e) => handleUpdate(activeCategory, item.item_index, 'model_no', e.target.value)}
                                            className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                            placeholder={config.model}
                                            disabled={isLocked}
                                            readOnly={isLocked}
                                        />
                                    </div>
                                )}
                                {config.serial && (
                                    <div>
                                        <label className="text-[10px] uppercase text-gray-500 font-bold">{config.serial}</label>
                                        <input
                                            type="text"
                                            value={item.serial_no || ''}
                                            onChange={(e) => handleUpdate(activeCategory, item.item_index, 'serial_no', e.target.value)}
                                            className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                            placeholder={config.serial}
                                            disabled={isLocked}
                                            readOnly={isLocked}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Dynamic Extra Specs */}
                            {Object.entries(config.extra).length > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(config.extra).map(([key, label]) => (
                                        <div key={key}>
                                            <label className="text-[10px] uppercase text-gray-500 font-bold">{label}</label>
                                            <input
                                                type="text"
                                                value={item.specifications?.[key] || ''}
                                                onChange={(e) => handleSpecUpdate(activeCategory, item.item_index, key, e.target.value)}
                                                className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                placeholder={label}
                                                disabled={isLocked}
                                                readOnly={isLocked}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!isQuantityOnly && (
                                <div>
                                    <label className="text-[10px] uppercase text-gray-500 font-bold">Current Status</label>
                                    <select
                                        value={item.status || 'working'}
                                        onChange={(e) => handleUpdate(activeCategory, item.item_index, 'status', e.target.value)}
                                        className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={isLocked}
                                    >
                                        <option value="working">Available</option>
                                        <option value="faulty">Faulty (Not Working)</option>
                                        <option value="maintenance">Under Maintenance</option>
                                    </select>
                                </div>
                            )}

                            <button
                                onClick={() => saveItem(activeCategory, item)}
                                disabled={uploadingId === `${activeCategory}-${item.item_index}` || isLocked}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {uploadingId === `${activeCategory}-${item.item_index}` ? 'Saving...' : <><MdSave /> Save Details</>}
                            </button>

                            {item.admin_remarks && (
                                <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
                                    <strong>Admin:</strong> {item.admin_remarks}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-blue-100 p-3 rounded-full">
                                <MdLock className="text-blue-600" size={28} />
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">Final Submit Confirmation</h2>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to submit? Once submitted, all details will be locked and you won't be able to make any changes without admin permission.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                disabled={submitting}
                                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFinalSubmit}
                                disabled={submitting}
                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? 'Submitting...' : 'Confirm Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
        </div>
    );
};

export default CenterFacilities;
